import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { ENVIRONMENT } from 'src/app/providers/environment';
import {
  SupportConversation,
  SupportConversationDetail,
  SupportConversationFilter,
  SupportConversationSummary,
} from 'src/app/types/admin-support';
import { Response, TableRequestParams } from 'src/app/types/general';

/**
 * The admin side of the support chat (GOO-466 API, GOO-467 page): the queue, reading and replying,
 * hand back and resolve. Also keeps the menu's count of conversations waiting for a person; there's
 * no polling, so it refreshes when the menu loads and after anything here that can change it.
 */
@Injectable({
  providedIn: 'root',
})
export class AdminSupportService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);

  /** How many conversations need follow-up; `0` until the first check. */
  readonly followUpCount = signal(0);

  private get url() {
    return this.env.server + '/admin/support/conversations';
  }

  /** The queue first, oldest request first; everything else most recently updated first. */
  getConversations(params: TableRequestParams & SupportConversationFilter) {
    return this.http.get<Response<SupportConversationSummary>>(this.url, { params: { ...params } });
  }

  /** Opening a conversation marks the merchant's messages in it as read for every admin. */
  getConversation(id: string) {
    return this.http.get<SupportConversationDetail>(this.url + '/' + id);
  }

  /** Reaches the merchant at once, and moves the conversation to the team. */
  reply(id: string, content: string) {
    return this.http.post<SupportConversation>(this.url + '/' + id + '/messages', { content });
  }

  /** The agent answers the merchant's next message. */
  handBack(id: string) {
    return this.http.post<SupportConversation>(this.url + '/' + id + '/hand-back', {});
  }

  resolve(id: string) {
    return this.http.post<SupportConversation>(this.url + '/' + id + '/resolve', {});
  }

  refreshFollowUpCount() {
    // `limit` can't be 0, and only the total is needed.
    this.getConversations({ status: 'needs_follow_up', skip: 0, limit: 1 }).subscribe({
      next: ({ total }) => this.followUpCount.set(total),
      // The count is a nicety; a failed check leaves it as it was.
      error: () => undefined,
    });
  }
}
